import {useMemo} from 'react';
import {LoadingBar} from '@/components/LoadingBar';
import {Typography} from '@/components/Typography';
import {MonthlyStoreCount} from '@/map/@types';
import {useMapDataSource} from '@/map/api';
import {PaddingBox} from '@/styled-components';
import {palette} from '@/styles';
import {
  GrayCenterBox, HighlightBox, SectionBlock, SectionSubTitle, SectionTitle
} from '../../StyledComponents';
import {TotalStoreCountTrendBiaxialLineChart} from '../Chart/TotalStoreCountTrendBiaxialLineChart';
import {EMPTY_ITEM_TYPES} from '../constants';
import {EmptyItem} from '../EmptyItem';
import {SalesDataProps} from '../interface';

export const TotalStoreCountTrend = ({
  isPending,
  error,
  data: rawdata,
}: SalesDataProps) => {
  const {data: dataSource} = useMapDataSource();
  const metadata = useMemo(
    () =>
      dataSource?.find(
        (source) =>
          source.key === 'kodata' && source.data.indexOf('매출 정보') !== -1
      ),
    [dataSource]
  );

  const data = useMemo(() => {
    return rawdata?.map((i, idx) => ({
      ...(i?.monthlyStoreCount || {}),
      idx,
    }));
  }, [rawdata]);

  return (
    <SectionBlock>
      <SectionTitle>총 매장 수 변동 추이</SectionTitle>
      <br />
      <SectionSubTitle>{`${metadata?.source || '카드사'}∙${
        metadata?.baseDate || ''
      }`}</SectionSubTitle>
      <PaddingBox top={16}>
        {isPending ? (
          <GrayCenterBox>
            <LoadingBar color={palette.textSecondary} />
          </GrayCenterBox>
        ) : error ? (
          <Typography variant="medium12" color={palette.textSecondary}>
            분석 실패
          </Typography>
        ) : !data ? (
          <EmptyItem type={EMPTY_ITEM_TYPES.Sales} />
        ) : (
          <TotalStoreCountTrendChartBox items={data} />
        )}
      </PaddingBox>
    </SectionBlock>
  );
};

const TotalStoreCountTrendChartBox = ({
  items,
}: {
  items: Array<
    MonthlyStoreCount & {
      idx: number;
    }
  >;
}) => {
  const compareList = items.map((_, idx) => `비교 상권 ${idx + 1}`);

  const maxData = items
    .map((item, index) => {
      const {monthlyStoreCount} = item;

      if (!monthlyStoreCount || !monthlyStoreCount.length) {
        return null;
      }

      const total = monthlyStoreCount.reduce(
        (acc, cur) => (acc += cur.count),
        0
      );

      const compareCount =
        monthlyStoreCount[0].count -
        monthlyStoreCount[monthlyStoreCount.length - 1].count;

      return {total, index, compareCount: Math.abs(compareCount)};
    })
    .filter((item) => item !== null);

  const filteredHasCountData = maxData.filter((i) => !!i.total);
  const isEmptyData = !filteredHasCountData.length;

  const isMaxDataEqual = isEmptyData
    ? false
    : maxData.every((item) => item.compareCount === maxData[0].compareCount);

  const maxCompareCountIndex = isEmptyData
    ? -1
    : filteredHasCountData.length > 1
    ? filteredHasCountData.reduce(
        (max, item) =>
          item.total > 0 && item.compareCount > max.compareCount ? item : max,
        filteredHasCountData[0]
      ).index
    : 0;

  return (
    <>
      <TotalStoreCountTrendBiaxialLineChart
        type="총매장수변동추이"
        data={items}
        compareList={compareList}
      />
      <PaddingBox top={16}>
        <HighlightBox>
          <Typography variant="medium12">
            {!compareList || isEmptyData ? (
              '비교 가능한 매출 정보가 없어요.'
            ) : isMaxDataEqual ? (
              '6개월 전 대비 총 매장 수 변동이 동일해요.'
            ) : (
              <>
                <span>
                  <Typography color={palette.primary500} fontWeight="700">
                    {compareList[maxCompareCountIndex]}
                  </Typography>
                </span>
                의 6개월 전 대비 총 매장 수 변동이 가장 커요.
              </>
            )}
          </Typography>
        </HighlightBox>
      </PaddingBox>
    </>
  );
};
